import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bug, CheckCheck, LifeBuoy, Lock, Plus, Send, X } from 'lucide-react';
import type { Ticket, TicketComment } from '@itr/shared';
import { formatRelative, isCustomerRole, SLA_TARGETS } from '@itr/shared';
import { api } from '../api/client';
import { AiAnalysisPanel } from '../components/AiAnalysis';
import { AiChatPanel } from '../components/AiChatPanel';
import { TicketSupportFee } from '../components/OneTimeCharges';
import { useAuth } from '../auth/AuthProvider';
import { useRealtimeEvent } from '../api/realtime';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  autoIntent,
  bulkResultMessage,
  runBulk,
  useFetch,
  useSelection,
  useToast,
} from '../components/ui';

const PRIORITY_INTENT = { urgent: 'bad', high: 'warn', normal: 'info', low: 'neutral' } as const;

export function ServiceDesk({ bugsOnly }: { bugsOnly?: boolean }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('open');
  const [priority, setPriority] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, reload } = useFetch(
    () =>
      api.get<Ticket[]>('/api/tickets', {
        state: state || undefined,
        priority: priority || undefined,
        type: bugsOnly ? 'bug' : undefined,
        limit: 100,
      }),
    [state, priority, bugsOnly],
    { refreshMs: 45_000 },
  );

  useRealtimeEvent(['tickets'], (event) => {
    if (event.type === 'ticket.created' || event.type === 'ticket.updated') reload();
  });

  const tickets = data ?? [];
  const toast = useToast();
  const canWrite = can('ticket:write');
  const selection = useSelection(tickets.map((t) => t.id));

  const bulkSetState = async (next: string) => {
    const result = await runBulk(selection.selectedIds, (id) => api.patch(`/api/tickets/${id}`, { state: next }));
    const { message, intent } = bulkResultMessage(next === 'closed' ? 'Closed' : 'Resolved', result);
    toast(message, intent);
    selection.clear();
    reload();
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">{bugsOnly ? 'Bug reports' : 'Service desk'}</h1>
          <div className="page-subtitle">
            {tickets.length} {state === 'open' ? 'open' : ''} {bugsOnly ? 'defects' : 'tickets'}
            {tickets.filter((t) => t.slaBreached).length > 0 && ` · ${tickets.filter((t) => t.slaBreached).length} breached SLA`}
          </div>
        </div>
        {can('ticket:write') && (
          <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus size={15} /> {bugsOnly ? 'Report a bug' : 'New ticket'}
          </button>
        )}
      </div>

      <div className="row" style={{ marginBottom: 14, gap: 4, flexWrap: 'wrap' }}>
        {(
          [
            ['open', 'Open'],
            ['new', 'New'],
            ['pending', 'Pending'],
            ['resolved', 'Resolved'],
            ['', 'All'],
          ] as const
        ).map(([value, label]) => (
          <button key={label} className={`btn btn-sm${state === value ? ' btn-primary' : ''}`} onClick={() => setState(value)}>
            {label}
          </button>
        ))}
        <span style={{ width: 12 }} />
        {['urgent', 'high', 'normal', 'low'].map((level) => (
          <button
            key={level}
            className={`btn btn-sm${priority === level ? ' btn-primary' : ''}`}
            onClick={() => setPriority(priority === level ? '' : level)}
            style={{ textTransform: 'capitalize' }}
          >
            {level}
          </button>
        ))}
      </div>

      {canWrite && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm" onClick={() => bulkSetState('resolved')}>
            <CheckCheck size={12} /> Mark resolved
          </button>
          <button className="btn btn-sm" onClick={() => bulkSetState('closed')}>
            <X size={12} /> Close
          </button>
        </SelectionBar>
      )}

      <div className="card">
        {loading && !data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={7} cols={5} />
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={bugsOnly ? <Bug size={20} /> : <LifeBuoy size={20} />}
            title={state === 'open' ? 'Queue is clear' : 'No tickets'}
            text={state === 'open' ? 'Nothing is waiting on you.' : 'Nothing matched these filters.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canWrite && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th style={{ width: 96 }}>Ref</th>
                  <th>Subject</th>
                  {bugsOnly && <th style={{ width: 100 }}>Severity</th>}
                  {bugsOnly && <th data-align="right" style={{ width: 70 }}>Hits</th>}
                  <th style={{ width: 92 }}>Priority</th>
                  <th style={{ width: 100 }}>State</th>
                  <th data-align="right" style={{ width: 130 }}>
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id} data-clickable="true" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                    {canWrite && (
                      <td>
                        <RowCheckbox selection={selection} id={ticket.id} />
                      </td>
                    )}
                    <td className="mono">{ticket.reference}</td>
                    <td>
                      <div style={{ fontWeight: 550 }}>{ticket.subject}</div>
                      {ticket.slaBreached && (
                        <span className="badge" data-intent="bad" style={{ marginTop: 3, height: 17, fontSize: 10.5 }}>
                          SLA breached
                        </span>
                      )}
                    </td>
                    {bugsOnly && (
                      <td>
                        <Badge intent={autoIntent(ticket.severity ?? 'minor')}>{ticket.severity ?? 'minor'}</Badge>
                      </td>
                    )}
                    {bugsOnly && (
                      <td data-align="right" className="nums">
                        {ticket.occurrences}
                      </td>
                    )}
                    <td>
                      <Badge intent={PRIORITY_INTENT[ticket.priority]}>{ticket.priority}</Badge>
                    </td>
                    <td>
                      <Badge intent={autoIntent(ticket.state)}>{ticket.state}</Badge>
                    </td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(ticket.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title={bugsOnly ? 'Report a bug' : 'New ticket'}>
        <TicketForm
          bug={bugsOnly}
          onCreated={(id) => {
            setCreateOpen(false);
            reload();
            navigate(`/tickets/${id}`);
          }}
        />
      </Drawer>
    </>
  );
}

/* ================================================================== */
/* Detail                                                             */
/* ================================================================== */

interface TicketDetailData extends Ticket {
  comments: TicketComment[];
  resource: { id: string; name: string } | null;
}

export function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, can } = useAuth();
  const toast = useToast();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);

  const { data, loading, error, reload } = useFetch(() => api.get<TicketDetailData>(`/api/tickets/${id}`), [id]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/api/tickets/${id}/comments`, { body: reply.trim(), internal });
      setReply('');
      reload();
    } catch (err) {
      toast('Could not post the reply', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setSending(false);
    }
  };

  const setField = async (patch: Record<string, unknown>) => {
    try {
      await api.patch(`/api/tickets/${id}`, patch);
      reload();
    } catch (err) {
      toast('Update failed', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  if (loading && !data) return <SkeletonTable rows={5} cols={2} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Ticket not found" />;

  const isStaff = !isCustomerRole(user?.role);

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div className="grow">
          <div className="row" style={{ gap: 8 }}>
            <span className="mono subtle">{data.reference}</span>
            <Badge intent={PRIORITY_INTENT[data.priority]}>{data.priority}</Badge>
            <Badge intent={autoIntent(data.state)}>{data.state}</Badge>
            {data.slaBreached && <Badge intent="bad">SLA breached</Badge>}
          </div>
          <h1 className="page-title" style={{ marginTop: 8 }}>
            {data.subject}
          </h1>
          <div className="page-subtitle">
            Opened {formatRelative(data.createdAt)}
            {data.dueAt && ` · due ${formatRelative(data.dueAt)}`}
          </div>
        </div>

        {isStaff && <TicketSupportFee ticket={data} />}

        {isStaff && can('ticket:write') && (
          <div className="row">
            <select className="select" style={{ width: 130 }} value={data.state} onChange={(e) => void setField({ state: e.target.value })}>
              {['new', 'open', 'pending', 'on_hold', 'resolved', 'closed'].map((s) => (
                <option key={s} value={s}>
                  {s.replace('_', ' ')}
                </option>
              ))}
            </select>
            <select
              className="select"
              style={{ width: 110 }}
              value={data.priority}
              onChange={(e) => void setField({ priority: e.target.value })}
            >
              {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
                <option key={p} value={p}>
                  {p} · {SLA_TARGETS[p].resolveMin / 60}h
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {data.body && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-body" style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.6 }}>
            {data.body}
          </div>
        </div>
      )}

      {data.type === 'bug' && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <Bug size={14} />
            <span className="card-title">Defect details</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 12 }}>
              reported {data.occurrences}×
            </span>
          </div>
          <div className="card-body col" style={{ gap: 12, fontSize: 13 }}>
            {([
              ['Component', data.component],
              ['Version', data.version],
              ['Steps to reproduce', data.stepsToReproduce],
              ['Expected', data.expectedBehaviour],
              ['Actual', data.actualBehaviour],
            ] as const).map(
              ([label, value]) =>
                value && (
                  <div key={label}>
                    <div className="subtle" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                      {label}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{value}</div>
                  </div>
                ),
            )}
          </div>
        </div>
      )}

      <AiAnalysisPanel subjectType="ticket" subjectId={data.id} />
      <AiChatPanel subjectType="ticket" subjectId={data.id} />

      <div className="card">
        <div className="card-header">
          <span className="card-title">Conversation</span>
          <span className="card-subtitle" style={{ marginLeft: 'auto' }}>
            {data.comments.length} message{data.comments.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="card-body col" style={{ gap: 14 }}>
          {data.comments.length === 0 && <span className="subtle" style={{ fontSize: 13 }}>No replies yet.</span>}

          {data.comments.map((comment) => (
            <div
              key={comment.id}
              style={{
                padding: '11px 13px',
                borderRadius: 'var(--itr-radius-sm)',
                // Internal notes are visually distinct so nobody mistakes one
                // for something the customer has already seen.
                background: comment.internal ? 'color-mix(in srgb, var(--itr-warning) 9%, var(--itr-surface-sunken))' : 'var(--itr-surface-sunken)',
                border: comment.internal ? 'var(--itr-border-w) dashed color-mix(in srgb, var(--itr-warning) 40%, transparent)' : 'none',
              }}
            >
              <div className="row" style={{ gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{comment.authorName}</span>
                {comment.internal && (
                  <span className="badge" data-intent="warn" style={{ height: 17, fontSize: 10.5 }}>
                    <Lock size={9} /> internal note
                  </span>
                )}
                <span className="grow" />
                <span className="subtle" style={{ fontSize: 11.5 }}>
                  {formatRelative(comment.createdAt)}
                </span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.55 }}>{comment.body}</div>
            </div>
          ))}

          {can('ticket:write') && (
            <div>
              <textarea
                className="textarea"
                placeholder="Write a reply…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => {
                  // Ctrl/Cmd+Enter sends; plain Enter is a newline, because
                  // support replies are usually multi-line.
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void send();
                }}
              />
              <div className="row" style={{ marginTop: 9 }}>
                {isStaff && (
                  <label className="row" style={{ gap: 7, cursor: 'pointer', fontSize: 12.5 }}>
                    <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                    Internal note (hidden from the customer)
                  </label>
                )}
                <span className="grow" />
                <span className="subtle" style={{ fontSize: 11.5 }}>
                  <span className="kbd">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}</span>
                  <span className="kbd" style={{ marginLeft: 3 }}>
                    ↵
                  </span>{' '}
                  to send
                </span>
                <button className="btn btn-primary" onClick={send} disabled={sending || !reply.trim()}>
                  <Send size={14} /> {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */

function TicketForm({ bug, onCreated }: { bug?: boolean; onCreated: (id: string) => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const { data: customers } = useFetch(() => api.get<Array<{ id: string; name: string }>>('/api/customers'), []);
  const [form, setForm] = useState({
    subject: '',
    body: '',
    priority: 'normal',
    customerId: '',
    severity: 'minor',
    component: '',
    stepsToReproduce: '',
    expectedBehaviour: '',
    actualBehaviour: '',
  });
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const ticket = await api.post<Ticket>('/api/tickets', {
        type: bug ? 'bug' : 'incident',
        subject: form.subject.trim(),
        body: form.body,
        priority: form.priority,
        customerId: form.customerId || null,
        ...(bug
          ? {
              severity: form.severity,
              component: form.component || null,
              stepsToReproduce: form.stepsToReproduce || null,
              expectedBehaviour: form.expectedBehaviour || null,
              actualBehaviour: form.actualBehaviour || null,
            }
          : {}),
      });
      onCreated(ticket.id);
    } catch (err) {
      toast('Could not create the ticket', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Subject</label>
        <input className="input" required autoFocus value={form.subject} onChange={set('subject')} placeholder="Short summary of the problem" />
      </div>

      <div className="field">
        <label className="field-label">Description</label>
        <textarea className="textarea" value={form.body} onChange={set('body')} placeholder="What happened, and what were you doing at the time?" />
      </div>

      {!isCustomerRole(user?.role) && (
        <div className="field">
          <label className="field-label">Customer</label>
          <select className="select" value={form.customerId} onChange={set('customerId')}>
            <option value="">Internal</option>
            {(customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {bug ? (
        <>
          <div className="field">
            <label className="field-label">Severity</label>
            <select className="select" value={form.severity} onChange={set('severity')}>
              <option value="blocker">Blocker — work cannot continue</option>
              <option value="major">Major — significant impact</option>
              <option value="minor">Minor — workaround exists</option>
              <option value="cosmetic">Cosmetic</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Component</label>
            <input className="input" value={form.component} onChange={set('component')} placeholder="e.g. billing portal, VOIP gateway" />
          </div>
          <div className="field">
            <label className="field-label">Steps to reproduce</label>
            <textarea className="textarea" value={form.stepsToReproduce} onChange={set('stepsToReproduce')} placeholder="1. …&#10;2. …" />
          </div>
          <div className="field">
            <label className="field-label">Expected</label>
            <input className="input" value={form.expectedBehaviour} onChange={set('expectedBehaviour')} />
          </div>
          <div className="field">
            <label className="field-label">Actual</label>
            <input className="input" value={form.actualBehaviour} onChange={set('actualBehaviour')} />
          </div>
        </>
      ) : (
        !isCustomerRole(user?.role) && (
          <div className="field">
            <label className="field-label">Priority</label>
            <select className="select" value={form.priority} onChange={set('priority')}>
              {(['urgent', 'high', 'normal', 'low'] as const).map((p) => (
                <option key={p} value={p}>
                  {p} — respond in {SLA_TARGETS[p].respondMin} min
                </option>
              ))}
            </select>
          </div>
        )
      )}

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Creating…' : bug ? 'Submit bug report' : 'Create ticket'}
      </button>
    </form>
  );
}
